import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PSWApiClient from '../clients/PSWApiClient'
import { ConfigContext } from '../store/ConfigContext'
import {
  ConfigurationDefinitionsKeys,
  FeatureFlagKeys
} from '../store/Constants'
import { useConfigurationDefinitions } from './useConfigurationDefinitions'
import { useExternalLink } from './useExternalLink'
import useLaunchdarkly from './useLaunchdarkly'

const DEFAULT_LOCALE_INFO = {
  language: 'en',
  country: 'us'
}

const hpSupportLinkCache = new Map()
const hpSupportLinkPromiseCache = new Map()

export const isHpSupportTemplateLink = (linkTemplate) => {
  return (
    typeof linkTemplate === 'string' &&
    linkTemplate.includes('[[PRODUCT_OID]]') &&
    linkTemplate.includes('[[LANGUAGE]]') &&
    linkTemplate.includes('[[COUNTRY_CODE]]')
  )
}

const normalizePageType = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  const match = /^(?:s-)?(\d{3})$/i.exec(value.trim())
  return match?.[1] ? `s-${match[1]}` : null
}

const getLegacyPageTypeSegment = (value) => {
  const normalizedPageType = normalizePageType(value)
  return normalizedPageType?.replace(/^s-/i, '') || null
}

const buildHpSupportCacheKey = ({
  stack,
  clientId,
  pswApiUrl,
  sku,
  printerOid,
  language,
  country,
  pageType,
  legacyLink,
  genericSupportUrl,
  featureFlagEnabled
}) => {
  return JSON.stringify({
    stack,
    clientId,
    pswApiUrl,
    sku,
    printerOid,
    language,
    country,
    pageType,
    legacyLink,
    genericSupportUrl,
    featureFlagEnabled
  })
}

const normalizeLocalePart = (value, fallback) => {
  if (typeof value !== 'string' || !value.trim()) {
    return fallback
  }

  return value.trim().toLowerCase()
}

const getPageTypeFromLinkTemplate = (linkTemplate) => {
  if (typeof linkTemplate !== 'string' || !linkTemplate) {
    return null
  }

  const pathMatch = /\/(\d{3})(?:[/?#]|$)/.exec(linkTemplate)
  if (pathMatch?.[1]) {
    return normalizePageType(pathMatch[1])
  }

  const queryMatch = /h_pagetype=((?:s-)?\d{3})/i.exec(linkTemplate)
  return normalizePageType(queryMatch?.[1])
}

export const useHpSupportRedirect = ({
  linkTemplate = null,
  pageType = null
} = {}) => {
  const { stack, localization, flags, pswApiUrl, printer, sku } =
    useContext(ConfigContext)
  const { pswApiClientId } = useLaunchdarkly()
  const { findConfigurationDefinitions } = useConfigurationDefinitions()
  const { findExternalLink } = useExternalLink()

  const hpSupportLocaleMap = findConfigurationDefinitions(
    ConfigurationDefinitionsKeys.hpSupportLocaleMap
  )

  const localeInfo = useMemo(() => {
    const language = normalizeLocalePart(
      localization?.language,
      DEFAULT_LOCALE_INFO.language
    )
    const country = normalizeLocalePart(
      localization?.country,
      DEFAULT_LOCALE_INFO.country
    )

    const localizationCode = `${language}-${country}`
    const mappedLocale = hpSupportLocaleMap?.[localizationCode]

    if (typeof mappedLocale === 'string') {
      const [mappedLanguage, mappedCountry] = mappedLocale
        .toLowerCase()
        .split(/[-_]/)

      if (mappedLanguage && mappedCountry) {
        return {
          language: mappedLanguage,
          country: mappedCountry
        }
      }
    }

    return {
      language,
      country
    }
  }, [hpSupportLocaleMap, localization?.country, localization?.language])

  const baseTemplate = useMemo(() => {
    return findExternalLink('HP_SUPPORT_SPECIFIC')?.link?.href || null
  }, [findExternalLink])

  const genericSupportUrl = useMemo(() => {
    return findExternalLink('HP_SUPPORT')?.link?.href || null
  }, [findExternalLink])

  const receivedLinkTemplate = isHpSupportTemplateLink(linkTemplate)
    ? linkTemplate
    : null
  const resolvedLinkTemplate = receivedLinkTemplate || baseTemplate
  const resolvedPageType = useMemo(
    () =>
      normalizePageType(
        pageType || getPageTypeFromLinkTemplate(resolvedLinkTemplate)
      ),
    [pageType, resolvedLinkTemplate]
  )

  const legacyLink = useMemo(() => {
    if (!printer?.oid || !resolvedLinkTemplate) {
      return null
    }

    const legacyPageTypeSegment = getLegacyPageTypeSegment(resolvedPageType)
    let finalLink = resolvedLinkTemplate
      .replaceAll('[[PRODUCT_OID]]', printer.oid)
      .replaceAll('[[LANGUAGE]]', localeInfo.language)
      .replaceAll('[[COUNTRY_CODE]]', localeInfo.country)

    if (legacyPageTypeSegment) {
      finalLink = finalLink
        .replace(/\/(\d{3})(?=([/?#]|$))/, `/${legacyPageTypeSegment}`)
        .replace(/h_pagetype=(?:s-)?(\d{3})/i, `h_pagetype=${resolvedPageType}`)
    }

    return finalLink
  }, [
    localeInfo.country,
    localeInfo.language,
    printer?.oid,
    resolvedLinkTemplate,
    resolvedPageType
  ])

  const featureFlagEnabled = Boolean(
    flags?.[FeatureFlagKeys.hpSupportResolutionWithPswApi]
  )
  const defaultSupportLink = featureFlagEnabled
    ? genericSupportUrl || null
    : legacyLink || genericSupportUrl || null
  const canResolveHpSupportLink = Boolean(
    legacyLink ||
      genericSupportUrl ||
      (featureFlagEnabled && sku && pswApiClientId)
  )

  const cacheKey = useMemo(
    () =>
      buildHpSupportCacheKey({
        stack,
        clientId: pswApiClientId,
        pswApiUrl,
        sku,
        printerOid: printer?.oid,
        language: localeInfo.language,
        country: localeInfo.country,
        pageType: resolvedPageType,
        legacyLink,
        genericSupportUrl,
        featureFlagEnabled
      }),
    [
      featureFlagEnabled,
      genericSupportUrl,
      legacyLink,
      localeInfo.country,
      localeInfo.language,
      printer?.oid,
      pswApiClientId,
      pswApiUrl,
      resolvedPageType,
      sku,
      stack
    ]
  )

  const getPswApiClient = useCallback(() => {
    if (!featureFlagEnabled || !pswApiClientId) {
      return null
    }

    return new PSWApiClient(stack, pswApiClientId, pswApiUrl)
  }, [featureFlagEnabled, pswApiClientId, pswApiUrl, stack])

  const logSupportResolutionWarning = useCallback(
    (response, error = null) => {
      console.warn('HP Support redirect resolution with PSW API failed', {
        sku,
        httpStatus: response?.status,
        redirectUrl: response?.data?.redirect_url,
        response: response?.data,
        error: error?.message
      })
    },
    [sku]
  )

  const resolvePswApiSupportUrl = useCallback(async () => {
    if (!sku) {
      return null
    }

    try {
      const client = getPswApiClient()

      if (!client) {
        return null
      }

      const response = await client.getHpSupportRedirect(
        sku,
        localeInfo.language,
        localeInfo.country,
        resolvedPageType
      )
      const redirectUrl = response?.data?.redirect_url || null

      if (!redirectUrl && response?.status >= 500) {
        logSupportResolutionWarning(response)
      }

      return redirectUrl
    } catch (error) {
      if (!error?.response || error?.response?.status >= 500) {
        logSupportResolutionWarning(error?.response, error)
      }

      return null
    }
  }, [
    getPswApiClient,
    localeInfo.country,
    localeInfo.language,
    logSupportResolutionWarning,
    resolvedPageType,
    sku
  ])

  const loadHpSupportLink = useCallback(async () => {
    if (!canResolveHpSupportLink) {
      return null
    }

    const cachedLink = hpSupportLinkCache.get(cacheKey)
    if (cachedLink) {
      return cachedLink
    }

    const pendingRequest = hpSupportLinkPromiseCache.get(cacheKey)
    if (pendingRequest) {
      return pendingRequest
    }

    const resolutionPromise = featureFlagEnabled
      ? resolvePswApiSupportUrl().then(
          (pswApiSupportUrl) => pswApiSupportUrl || defaultSupportLink
        )
      : Promise.resolve(defaultSupportLink)

    hpSupportLinkPromiseCache.set(cacheKey, resolutionPromise)

    try {
      const resolvedLink = await resolutionPromise
      if (resolvedLink) {
        hpSupportLinkCache.set(cacheKey, resolvedLink)
      }
      return resolvedLink || null
    } finally {
      if (hpSupportLinkPromiseCache.get(cacheKey) === resolutionPromise) {
        hpSupportLinkPromiseCache.delete(cacheKey)
      }
    }
  }, [
    cacheKey,
    canResolveHpSupportLink,
    defaultSupportLink,
    featureFlagEnabled,
    resolvePswApiSupportUrl
  ])

  const [hpSupportLink, setHpSupportLink] = useState(() => {
    if (!canResolveHpSupportLink) {
      return null
    }

    return hpSupportLinkCache.get(cacheKey) || defaultSupportLink
  })

  useEffect(() => {
    if (!canResolveHpSupportLink) {
      setHpSupportLink(null)
      return undefined
    }

    const cachedLink = hpSupportLinkCache.get(cacheKey)
    const initialLink = cachedLink || defaultSupportLink

    setHpSupportLink(initialLink)

    if (cachedLink || !featureFlagEnabled) {
      return undefined
    }

    let isMounted = true

    void loadHpSupportLink().then((resolvedLink) => {
      if (isMounted) {
        setHpSupportLink(resolvedLink)
      }
    })

    return () => {
      isMounted = false
    }
  }, [
    cacheKey,
    canResolveHpSupportLink,
    defaultSupportLink,
    featureFlagEnabled,
    loadHpSupportLink
  ])

  const resolveHpSupportLink = useCallback(async () => {
    const resolvedLink = await loadHpSupportLink()

    if (resolvedLink) {
      setHpSupportLink(resolvedLink)
    }

    return resolvedLink
  }, [loadHpSupportLink])

  return {
    canResolveHpSupportLink,
    hpSupportLink,
    resolveHpSupportLink
  }
}

export default useHpSupportRedirect
